import { ChangelogDevtool } from "components/Devtools/ChangelogDevtool";
import { DocsDevtool } from "components/Devtools/DocsDevtool";
import { SandboxInfo } from "components/Devtools/SandboxInfo";
import { SetupTasksDevTool } from "components/Devtools/SetupTasksDevTool";
import { TaskPortDevtool } from "components/Devtools/TaskPortDevtool";
import { TestsDevtool } from "components/Devtools/TestsDevtool/TestsDevtool";
import { UnassignedPortDevtool } from "components/Devtools/UnassignedPortDevtool";
import { DevtoolWrapper } from "components/Devtools/common/DevtoolWrapper";
import type { Tab, TabbedPanel } from "features/types/tabs";
import type { IconType } from "prism-react";
import React, { Suspense, createContext } from "react";

const EnvSetupDevtool = React.lazy(() =>
  import("components/Devtools/EnvSetupDevtool/EnvSetupDevtool").then((m) => ({
    default: m.EnvSetupDevtool,
  })),
);

const VirtualMachineDevtool = React.lazy(() =>
  import("components/Devtools/VirtualMachineDevtool").then((m) => ({
    default: m.VirtualMachineDevtool,
  })),
);

type ActionItem =
  | { tooltip: string; id: string; onClick: () => void; icon: IconType }
  | { id: string; tooltip: string; icon: IconType; href: string };

export const devtoolActionsContext = createContext({
  addItem: (_element: ActionItem) => {
    return () => {};
  },
  items: [] as ActionItem[],
});

export function getTabName(tab: Tab) {
  const type = tab.type;

  switch (type) {
    case "SANDBOX_INFO": {
      return tab.isCloud ? "Devbox Info" : "Sandbox Info";
    }
    case "PR_INFO": {
      return "PR Info";
    }
    case "FILE": {
      return tab.filepath;
    }
    case "DIFF": {
      return tab.filepath;
    }
    case "GIT_LOG":
      return "Git Log";
    case "SETUP_TASKS":
      return "Setup tasks";
    case "VIRTUAL_MACHINE":
      return "VM usage & specs";
    case "TASK_LOG":
      return "Task Log";
    case "TASK_PORT":
      return "Preview";
    case "TERMINAL":
      return "Terminal";
    case "NEW_TERMINAL":
      return "Terminal";
    case "UNASSIGNED_PORT":
      return "Preview";
    case "DOCS":
      return "Documentation";
    case "CHANGELOG":
      return "Changelog";
    case "ENV_SETUP":
      return "Project setup";
    case "TESTS":
      return "Tests";
    default:
      // eslint-disable-next-line no-case-declarations
      const exhaustiveCheck: never = type;
      throw new Error(`Unhandled tab type: ${exhaustiveCheck}`);
  }
}

export function getPanelContent(tab: Tab, panel: TabbedPanel) {
  const type = tab.type;
  const props = {
    isActiveTab: panel.activeTabId === tab.id,
    panelId: panel.id,
  };

  switch (type) {
    case "SANDBOX_INFO": {
      return <SandboxInfo {...props} tab={tab} />;
    }
    case "SETUP_TASKS":
      return <SetupTasksDevTool {...props} tab={tab} />;
    case "VIRTUAL_MACHINE":
      return (
        <Suspense
          fallback={
            <DevtoolWrapper variant="setup">
              <></>
            </DevtoolWrapper>
          }
        >
          <VirtualMachineDevtool {...props} tab={tab} />
        </Suspense>
      );
    case "TASK_PORT":
      return <TaskPortDevtool {...props} tab={tab} />;
    case "UNASSIGNED_PORT":
      return <UnassignedPortDevtool {...props} tab={tab} />;
    case "DOCS": {
      return <DocsDevtool {...props} tab={tab} />;
    }
    case "CHANGELOG": {
      return <ChangelogDevtool {...props} tab={tab} />;
    }
    case "ENV_SETUP": {
      return (
        <Suspense
          fallback={
            <DevtoolWrapper variant="setup">
              <></>
            </DevtoolWrapper>
          }
        >
          <EnvSetupDevtool dependencies={tab.dependencies} tab={tab} />
        </Suspense>
      );
    }
    case "TESTS": {
      return <TestsDevtool {...props} tab={tab} />;
    }
    default: {
      return null;
    }
  }
}
